import type { Identity, ShellAuth } from '@thecvlb/auth-web';
import type { Persistor } from 'redux-persist';

import { RootState, store } from 'store';
import { clearUser, setLoginServiceSession } from 'store/user/userSlice';

import { logDataDogEvent } from './datadog';
import { sessionKeyOfIdentity } from './loginService';
import { session, SESSION_KEYS } from './storage';

/** How long the first paint waits for the login service to restore a session. */
const RESTORE_TIMEOUT_MS = 5_000;

const savedUser = () => (store.getState() as RootState).user;

const heldByLoginService = () => !!savedUser().authSessionKey;

/** A token the login service did not install: verify-identity link, `?login-token=`, sign-up, legacy. */
const heldByPortal = () => {
  const { accessToken, authSessionKey } = savedUser();
  return !!accessToken && !authSessionKey;
};

/** Whether the saved profile, if any, belongs to someone other than this identity. */
const savedProfileIsSomeoneElse = (identity: Identity) => {
  const { _id, userId } = savedUser();
  const savedId = userId || _id;

  return !!savedId && identity.subject !== savedId;
};

/** Off until the backend accepts impersonation tokens (LMD-696); before that every call would 401. */
export const IMPERSONATION_ACCEPTED = false;

/**
 * Marks the tab as staff viewing a patient, like `?login-token=`: `VIEW_AS_PATIENT` drives the staff
 * UI, `STAFF_ID` (the actor's LifeMD user id) is sent as `impersonation-mode-by`. Set before the
 * patient reaches the store, so the first render already knows (LMD-1820).
 */
export const markViewAsPatient = (identity: Identity) => {
  session.set(SESSION_KEYS.VIEW_AS_PATIENT, 'true');
  if (identity.actor?.subject) {
    session.set(SESSION_KEYS.STAFF_ID, identity.actor.subject);
  }
};

export const clearViewAsPatient = () => {
  session.remove(SESSION_KEYS.VIEW_AS_PATIENT);
  session.remove(SESSION_KEYS.STAFF_ID);
};

const heldImpersonation = () => savedUser().authSessionKey.includes(':impersonation:');

/** Bumped per event and restore; an overtaken sync stands down, so a slow `getToken()` can't win. */
let generation = 0;

/**
 * Mirrors the login service's session into the store (LMD-682), so `App`'s usual bootstrap runs.
 * Never replaces or clears a session the portal signed in itself, and clears a saved profile of
 * someone else first. Impersonation: see `IMPERSONATION_ACCEPTED`.
 */
const sync = async (auth: ShellAuth, identity: Identity | null, startedIn: number) => {
  if (!identity) {
    if (heldByLoginService()) {
      // The staff marking ends with the impersonation.
      if (heldImpersonation()) {
        clearViewAsPatient();
      }
      store.dispatch(clearUser());
    }
    return;
  }

  const isImpersonation = identity.kind === 'impersonation';

  if (isImpersonation && (!IMPERSONATION_ACCEPTED || !identity.actor?.subject)) {
    logDataDogEvent({
      level: 'warn',
      message: IMPERSONATION_ACCEPTED
        ? 'Login service impersonation session refused: it names no acting staff member'
        : 'Login service impersonation session refused until the backend accepts it (LMD-696)'
    });
    return;
  }

  if (heldByPortal()) {
    return;
  }

  const accessToken = await auth.getToken();

  if (!accessToken || startedIn !== generation || heldByPortal()) {
    return;
  }

  if (savedProfileIsSomeoneElse(identity)) {
    store.dispatch(clearUser());
  }

  if (isImpersonation) {
    markViewAsPatient(identity);
  }

  store.dispatch(
    setLoginServiceSession({
      accessToken,
      sessionKey: sessionKeyOfIdentity(identity),
      source: identity.authSource
    })
  );
};

/** Resolves once redux-persist has rehydrated; a session restored earlier would be overwritten. */
export const waitForRehydration = (persistor: Persistor) =>
  new Promise<void>((resolve) => {
    if (persistor.getState().bootstrapped) {
      resolve();
      return;
    }

    const unsubscribe = persistor.subscribe(() => {
      if (persistor.getState().bootstrapped) {
        unsubscribe();
        resolve();
      }
    });
  });

/**
 * Subscribes and restores any existing session. Never rejects and is bounded by `timeoutMs`, so the
 * first render knows whether the patient is signed in.
 */
export const connectLoginService = async (auth: ShellAuth, timeoutMs = RESTORE_TIMEOUT_MS) => {
  auth.onChange((event, identity) => {
    const startedIn = ++generation;

    // Another person: drop the previous one first, unless the portal signed this session in.
    if (event === 'identity-changed' && !heldByPortal()) {
      store.dispatch(clearUser());
    }
    void sync(auth, identity, startedIn).catch(() => {});
  });

  // A failure or hang counts as no session; a late result may still install, like a late event.
  const startedIn = ++generation;
  const restore = auth
    .getSession()
    .then((session) => sync(auth, session.user, startedIn))
    .catch(() => {});
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, timeoutMs));

  await Promise.race([restore, timeout]);
};
